import React, { createContext, useState, useEffect } from 'react';
import { jwtDecode } from 'jwt-decode';
import { User } from '../types/auth';
import { AppLoader } from '@/components/ui/app-loader';

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  login: (accessToken: string, refreshToken: string) => void;
  logout: () => void;
  isLoading: boolean;
}

export const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Check for existing token on mount
  useEffect(() => {
    const checkAuth = async () => {
      const accessToken = localStorage.getItem('accessToken');
      if (accessToken) {
        try {
          const decodedUser = jwtDecode<User>(accessToken);
          setUser(decodedUser);
          setIsAuthenticated(true);
        } catch (error) {
          console.error('Error decoding JWT token:', error);
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
        }
      }
      setIsLoading(false);
    };

    checkAuth();
  }, []);

  const login = (accessToken: string, refreshToken: string) => {
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    const decodedUser = jwtDecode<User>(accessToken);
    setUser(decodedUser);
    setIsAuthenticated(true);
    setIsLoading(false);
  };

  const logout = () => {
    // First navigate to login page directly to avoid the 404 flash
    // This bypasses React Router completely
    window.location.replace('/auth/login');

    // The state cleanup will happen when the page reloads
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
  };

  if (isLoading) {
    return <AppLoader message="Initializing app..." />;
  }

  return (
    <AuthContext.Provider value={{ user, isAuthenticated, login, logout, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
};
